import { jsx as _jsx } from "react/jsx-runtime";
import { Testids } from '@mono/ui-selectors';
import { BookOpen, ChevronDown, ChevronRight, ClipboardCheck, ClipboardList, DollarSign, FileSpreadsheet, FileText, FolderOpen, GitBranch, History, LayoutDashboard, Library, MessageSquare, ShieldCheck, Sparkles, Users } from 'lucide-react';
const icon = 'h-4 w-4 flex-shrink-0';
export const PROPOSAL_VOLUMES_VISIBLE = 5;
/** Custom → Sparkles (no colour), workbook → FileSpreadsheet, template → ClipboardList, else BookOpen. */ export function proposalVolumeIcon(v) {
    if (v.isCustom) return /*#__PURE__*/ _jsx(Sparkles, {
        "aria-hidden": "true",
        className: icon
    });
    switch(v.kind){
        case 'workbook':
            return /*#__PURE__*/ _jsx(FileSpreadsheet, {
                "aria-hidden": "true",
                className: icon
            });
        case 'template':
            return /*#__PURE__*/ _jsx(ClipboardList, {
                "aria-hidden": "true",
                className: icon
            });
        default:
            return /*#__PURE__*/ _jsx(BookOpen, {
                "aria-hidden": "true",
                className: icon
            });
    }
}
/** §4.3 Workspaces group (base `/proposal/:id/v2`). */ export const PROPOSAL_WORKSPACE_DEFS = [
    {
        id: 'home',
        label: 'Home',
        path: 'home',
        icon: /*#__PURE__*/ _jsx(LayoutDashboard, {
            className: icon
        })
    },
    {
        id: 'compliance',
        label: 'Compliance',
        path: 'compliance',
        icon: /*#__PURE__*/ _jsx(ClipboardCheck, {
            className: icon
        }),
        countKey: 'compliance',
        countLabel: 'requirements'
    },
    {
        id: 'capability-matrix',
        label: 'Capability Matrix',
        path: 'capability-matrix',
        icon: /*#__PURE__*/ _jsx(ShieldCheck, {
            className: icon
        })
    },
    {
        id: 'reviews',
        label: 'Reviews',
        path: 'reviews',
        icon: /*#__PURE__*/ _jsx(Users, {
            className: icon
        }),
        countKey: 'reviews',
        countLabel: 'open reviews',
        // Journeys (growth_home_02, growth_prop_12) click this testid.
        testId: Testids.proposalReviews.navLink
    },
    {
        id: 'chat-history',
        label: 'Chat History',
        path: 'chat-history',
        icon: /*#__PURE__*/ _jsx(MessageSquare, {
            className: icon
        })
    }
];
/** Documents group: rendered only when `proposal_documents_hub_v1` is on (PRP-22 builds the route). */ export const PROPOSAL_DOCUMENT_DEFS = [
    {
        id: 'documents',
        label: 'All documents',
        icon: /*#__PURE__*/ _jsx(FolderOpen, {
            className: icon
        }),
        path: 'documents',
        countKey: 'documents',
        countLabel: 'documents'
    }
];
/** §4.3 Sources group. */ export const PROPOSAL_SOURCE_DEFS = [
    {
        id: 'files',
        label: 'Files',
        path: 'sources/files',
        icon: /*#__PURE__*/ _jsx(FileText, {
            className: icon
        }),
        countKey: 'files',
        countLabel: 'files'
    },
    {
        id: 'clins',
        label: 'CLINs',
        path: 'sources/clins',
        icon: /*#__PURE__*/ _jsx(Library, {
            className: icon
        }),
        countKey: 'clins',
        countLabel: 'CLINs'
    },
    {
        id: 'lcats',
        label: 'Labor Categories',
        path: 'sources/lcats',
        icon: /*#__PURE__*/ _jsx(Users, {
            className: icon
        }),
        countKey: 'lcats',
        countLabel: 'labor categories'
    },
    {
        id: 'pricing',
        label: 'Pricing',
        path: 'sources/pricing',
        icon: /*#__PURE__*/ _jsx(DollarSign, {
            className: icon
        })
    },
    {
        id: 'pursuit',
        label: 'From the pursuit',
        path: 'sources/pursuit',
        icon: /*#__PURE__*/ _jsx(GitBranch, {
            className: icon
        }),
        countKey: 'pursuit',
        countLabel: 'items carried in'
    },
    {
        id: 'team',
        label: 'Team',
        path: 'sources/team',
        icon: /*#__PURE__*/ _jsx(Users, {
            className: icon
        })
    },
    {
        id: 'history',
        label: 'Amendment History',
        path: 'sources/history',
        icon: /*#__PURE__*/ _jsx(History, {
            className: icon
        }),
        countKey: 'amendments',
        countLabel: 'amendments'
    }
];
export function buildProposalNavGroups(args) {
    const { volumes, volumesLoading, volumesExpanded, onToggleVolumes, documentsHub } = args;
    const sourceDefs = PROPOSAL_SOURCE_DEFS.filter((d)=>(d.id !== 'pursuit' || args.hasPursuit) && (d.id !== 'pricing' || args.canReadPricing));
    let volumeItems;
    if (volumesLoading && volumes.length === 0) {
        volumeItems = [
            {
                id: 'volumes-loading',
                label: 'Loading volumes…'
            }
        ];
    } else if (volumes.length === 0) {
        volumeItems = [
            {
                id: 'volumes-empty',
                label: 'No volumes yet'
            }
        ];
    } else {
        const shown = volumesExpanded ? volumes : volumes.slice(0, PROPOSAL_VOLUMES_VISIBLE);
        volumeItems = shown.map((v)=>({
                id: `volume-${v.volumeId}`,
                label: v.displayName,
                icon: proposalVolumeIcon(v),
                path: `volume/${v.volumeId}`
            }));
        if (volumes.length > PROPOSAL_VOLUMES_VISIBLE) {
            volumeItems.push({
                id: 'volumes-more',
                label: volumesExpanded ? 'Show less' : `Show ${volumes.length - PROPOSAL_VOLUMES_VISIBLE} more`,
                icon: volumesExpanded ? /*#__PURE__*/ _jsx(ChevronDown, {
                    className: "h-4 w-4"
                }) : /*#__PURE__*/ _jsx(ChevronRight, {
                    className: "h-4 w-4"
                }),
                onClick: onToggleVolumes,
                testId: Testids.recordNav.volumesMore
            });
        }
    }
    return [
        {
            id: 'workspaces',
            label: 'Workspaces',
            items: PROPOSAL_WORKSPACE_DEFS
        },
        {
            id: 'volumes',
            label: 'Volumes',
            items: volumeItems
        },
        ...documentsHub ? [
            {
                id: 'documents',
                label: 'Documents',
                defaultOpen: true,
                items: PROPOSAL_DOCUMENT_DEFS
            }
        ] : [],
        {
            id: 'sources',
            label: 'Sources',
            items: documentsHub ? sourceDefs.map((d)=>d.id === 'files' ? {
                    ...d,
                    path: 'documents?section=solicitation'
                } : d) : sourceDefs
        }
    ];
}
export const PROPOSAL_SECTION_LABELS = {
    home: 'Home',
    compliance: 'Compliance',
    'capability-matrix': 'Capability Matrix',
    reviews: 'Reviews',
    'chat-history': 'Chat History',
    documents: 'Documents',
    files: 'Files',
    clins: 'CLINs',
    lcats: 'Labor Categories',
    pricing: 'Pricing',
    pursuit: 'From the pursuit',
    team: 'Team',
    history: 'Amendment History',
    settings: 'Settings'
};
/** "36C10B27R0031 · due 14 Apr 2027". */ export function proposalSubtitle(project) {
    const number = project?.solicitation?.solicitationNumber;
    const deadline = project?.solicitation?.proposalDeadline;
    const date = deadline ? new Date(deadline) : undefined;
    // A date-only value ("2027-04-14") parses as UTC midnight; format it in UTC so
    // viewers west of UTC don't see the day before.
    const dateOnly = !!deadline && /^\d{4}-\d{2}-\d{2}$/.test(deadline);
    const due = date && !Number.isNaN(date.getTime()) ? `due ${date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        ...dateOnly ? {
            timeZone: 'UTC'
        } : {}
    })}` : undefined;
    return [
        number,
        due
    ].filter(Boolean).join(' · ') || undefined;
}
